import React, { ButtonHTMLAttributes, CSSProperties } from 'react';
import styled from 'styled-components';

import { Styling, Colors, Animations } from '../../constants';
import { filterI18nProps } from '../../utils/styledComponentsUtils';

const getPlainOnWhiteDisabledStyle = (props: IProps) => {
  if (props.disabled) {
    return `
      color: ${Colors.BLACK_50};
      cursor: not-allowed;
    `;
  }

  return `
    &:hover, &:focus {
      cursor: pointer;
      background-color: ${Colors.BLACK_10};
      transition: background-color ${Animations.DEFAULT_TRANSITION}, color ${Animations.DEFAULT_TRANSITION} !important;
    }

    &:active {
      background-color: ${Colors.BLACK_10};
    }
  `;
};

const getPlainOnBlackDisabledStyle = (props: IProps) => {
  if (props.disabled) {
    return `
      cursor: not-allowed;
      color: #afafaf;
    `;
  }

  return `
    &:hover, &:focus {
      cursor: pointer;
      background-color: ${Colors.WHITE_25};
      transition: background-color ${Animations.DEFAULT_TRANSITION}, color ${Animations.DEFAULT_TRANSITION} !important;
    }

    &:active {
      background-color: ${Colors.WHITE_30};
    }
  `;
};

const getPrimaryOnWhiteDisabledStyle = (props: IProps) => {
  if (props.disabled) {
    return `
      cursor: not-allowed;
      border-style: solid;
      border-color: rgba(0,0,0,0);
      border-width: 1px;
      color: ${Colors.WHITE_80};
      background-color: ${Colors.BLACK_40};
    `;
  }

  return `
    &:hover, &:focus {
      cursor: pointer;
      border-style: solid;
      border-color: ${Colors.PRIMARY_DARKER_1} !important;
      border-width: 1px;
      background-color: ${Colors.PRIMARY_DARKER_1};
      transition: background-color ${Animations.DEFAULT_TRANSITION}, color ${Animations.DEFAULT_TRANSITION} !important;
    }

    &:active {
      border-style: solid;
      border-color: ${Colors.PRIMARY_DARKER_2} !important;
      border-width: 1px;
      background-color: ${Colors.PRIMARY_DARKER_2};
    }
  `;
};

const getPrimaryOnBlackDisabledStyle = (props: IProps) => {
  if (props.disabled) {
    return `
      cursor: not-allowed;
      color: ${Colors.WHITE_30};
      background-color: ${Colors.WHITE_20};
    `;
  }

  return `
    &:hover, &:focus {
      cursor: pointer;
      background-color: ${Colors.PRIMARY_DARKER_1};
      transition: background-color ${Animations.DEFAULT_TRANSITION}, color ${Animations.DEFAULT_TRANSITION} !important;
    }

    &:active {
      background-color: ${Colors.PRIMARY_DARKER_2};
    }
  `;
};

const getTagButton = (props: IProps) => {
  if (props.disabled) {
    return `
      cursor: not-allowed;
      color: #afafaf;
    `;
  }

  return `
    &:hover, &:focus {
      cursor: pointer;
      background-color: ${Colors.WHITE_25};
      transition: background-color ${Animations.DEFAULT_TRANSITION}, color ${Animations.DEFAULT_TRANSITION} !important;
    }

    &:active {
      background-color: ${Colors.WHITE_30};
    }
  `;
};

const getJobsButtonDisabledStyle = (props: IProps) => {
  if (props.disabled) {
    return `
      cursor: not-allowed;
      border-style: solid;
      border-color: rgba(0,0,0,0);
      border-width: 1px;
      color: ${Colors.WHITE_80};
      background-color: ${Colors.BLACK_40};
    `;
  }

  return `
    &:hover, &:active {
      cursor: pointer;
      border-style: solid;
      border-color: ${Colors.PRIMARY_DARKER_1} !important;
      border-width: 1px;
      background-color: ${Colors.PRIMARY_DARKER_1};
      transition: background-color ${Animations.DEFAULT_TRANSITION}, color ${Animations.DEFAULT_TRANSITION} !important;
    }
  `;
};

const getJobsErrorStyle = (props: IProps) => {
  if (props.disabled) {
    return `
      cursor: not-allowed;
      color: ${Colors.WHITE_30};
      background-color: ${Colors.WHITE_20};
    `;
  }

  return `
    &:hover, &:focus {
      cursor: pointer;
      background-color: ${Colors.PRIMARY_DARKER_1};
      transition: background-color ${Animations.DEFAULT_TRANSITION}, color ${Animations.DEFAULT_TRANSITION} !important;
    }

    &:active {
      background-color: ${Colors.PRIMARY_DARKER_2};
    }
  `;
};

const getWarningStyle = (props: IProps) => {
  if (props.disabled) {
    return `
      color: ${Colors.BLACK_50};
      cursor: not-allowed;
    `;
  }

  return `
    &:hover, &:focus {
      cursor: pointer;
      background-color: ${Colors.BLACK_10};
      transition: background-color ${Animations.DEFAULT_TRANSITION}, color ${Animations.DEFAULT_TRANSITION} !important;
    }

    &:active {
      background-color: ${Colors.BLACK_10};
    }
  `;
};

export const BaseStyle = styled.button<IProps>`
  border-radius: ${Styling.DEFAULT_BORDER_RADIUS}px;
  padding: 8px;
  border: none;
  font-size: 14px;
  transition: background-color ${Animations.DEFAULT_TRANSITION},
    color ${Animations.DEFAULT_TRANSITION} !important;
  display: flex;
  align-items: center;
  justify-content: center;

  ${(props) => (props.width ? `width: ${props.width}` : '')}
`;

const TagButton = styled(BaseStyle)`
  color: ${Colors.RED_2};
  background-color: transparent;
  font-size: 10px;
  padding-top: 2px;
  padding-bottom: 0px;
  padding-left: 2px;
  padding-right: 0px;
  ${getTagButton}
`;

const JobsButton = styled(BaseStyle)`
  gap: 10px;
  padding-top: 8px;
  padding-bottom: 8px;
  padding-left: 12px;
  padding-right: 12px;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
  border-radius: 4px;
  border-style: solid;
  border-color: ${Colors.WHITE_100};
  border-width: 1px;
  background-color: ${Colors.WHITE_100};
  color: black;
  ${getJobsButtonDisabledStyle}
`;

const JobsErrorButton = styled(BaseStyle)`
  gap: 10px;
  padding-top: 8px;
  padding-bottom: 8px;
  padding-left: 12px;
  padding-right: 12px;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
  border-radius: 4px;
  border-style: solid;
  border-color: ${Colors.ERROR};
  border-width: 1px;
  background-color: ${Colors.ERROR};
  color: white;
 ${getJobsErrorStyle}
`;

const PlainOnWhite = styled(BaseStyle)`
  color: black;
  background-color: ${Colors.BLACK_05};

  ${getPlainOnWhiteDisabledStyle}
`;

const PlainOnBlack = styled(BaseStyle)`
  color: white;
  background-color: ${Colors.WHITE_20};

  ${getPlainOnBlackDisabledStyle}
`;

const PrimaryOnWhite = styled(BaseStyle)`
  border-style: solid;
  border-color: ${Colors.PRIMARY};
  border-width: 1px;
  background-color: ${Colors.PRIMARY};
  color: white;

  ${getPrimaryOnWhiteDisabledStyle}
`;

const PrimaryOnBlack = styled(BaseStyle)`
  background-color: ${Colors.PRIMARY};
  color: white;

  ${getPrimaryOnBlackDisabledStyle}
`;

const TransparentOnBlack = styled(BaseStyle)`
  color: white;
  background-color: transparent;

  ${getPlainOnBlackDisabledStyle}
`;

const TransparentOnWhite = styled(BaseStyle)`
  color: black;
  background-color: transparent;

  ${getPlainOnBlackDisabledStyle}
`;

const getPlainOnPaperDisabledStyle = (props: IProps) => {
  if (props.disabled) {
    return `
      cursor: not-allowed;
      color: ${Colors.BLACK_50};
    `;
  }

  return `
    &:hover, &:focus {
      cursor: pointer;
      background-color: ${Colors.BLACK_02};
      transition: background-color ${Animations.DEFAULT_TRANSITION}, color ${Animations.DEFAULT_TRANSITION} !important;
    }

    &:active {
      background-color: ${Colors.BLACK_05};
    }
  `;
};

const PlainOnPaper = styled(BaseStyle)`
  color: black;
  background-color: white;
  border: 1px solid ${Colors.BLACK_40};

  ${getPlainOnPaperDisabledStyle}
`;

export type StyleVariant = | 'plainOnWhite'
  | 'plainOnBlack'
  | 'plainOnPaper'
  | 'primaryOnWhite'
  | 'primaryOnBlack'
  | 'transparentOnBlack'
  | 'transparentOnWhite'
  | 'tagButton'
  | 'jobsButton'
  | 'jobsErrorButton';

interface IProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: StyleVariant;
  width?: string;
  style?: CSSProperties;
}

const getButtonStyle = (props: IProps) => {
  const { variant = 'plainOnWhite', ...restProps } = props;
  const filteredProps = filterI18nProps(restProps);

  switch (variant) {
    case 'tagButton':
      return <TagButton {...filteredProps} />;
    case 'jobsButton':
      return <JobsButton {...filteredProps} />;
    case 'jobsErrorButton':
      return <JobsErrorButton {...filteredProps} />;
    case 'plainOnPaper':
      return <PlainOnPaper {...filteredProps} />;
    case 'transparentOnBlack':
      return <TransparentOnBlack {...filteredProps} />;
    case 'transparentOnWhite':
      return <TransparentOnWhite {...filteredProps} />;
    case 'primaryOnBlack':
      return <PrimaryOnBlack {...filteredProps} />;
    case 'primaryOnWhite':
      return <PrimaryOnWhite {...filteredProps} />;
    case 'plainOnBlack':
      return <PlainOnBlack {...filteredProps} />;
    case 'plainOnWhite':
    default:
      return <PlainOnWhite {...filteredProps} />;
  }
};

const Button: React.FC<IProps> = (props: IProps) => getButtonStyle(props);

export default Button;
